import React, { useContext, useEffect, useReducer } from 'react';
import { changeDishPositionReducer } from './changeDishPositionReducer';
import { ChangeDishPositionActionTypes } from './changeDishPositionActions';
import { AppContext, IAppContext } from 'appState/appContext';
import {
	ChangeDishPositionContext,
	changeDishPositionInitialState,
	TChangeDishPositionSelectedItem,
	TTypeOfChangeDishPositionModalWindow,
} from './changeDishPositionContext';
import { IItemForGetShortInfo, IDishPositionItem, IGetDishPositionList, IGetDishPositionListArguments } from 'types';
import { useFetchData } from 'hooks/useFetchData';
import { getDishPositionList } from 'backend/dishes';

const ChangeDishPositionState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(changeDishPositionReducer, changeDishPositionInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const { response, loading, refetch } = useFetchData<IGetDishPositionList, IGetDishPositionListArguments>(
		state.IDSelectedSubCategory ? getDishPositionList : null,
		{
			restaurantId: state.IDSelectedRestaurant || '',
			categoryId: state.IDSelectedCategory || '',
			subCategoryId: state.IDSelectedSubCategory || '',
		},
	);

	const setIDCustomer = (value: TChangeDishPositionSelectedItem) => {
		dispatch({
			type: ChangeDishPositionActionTypes.SET_ID_CUSTOMER,
			payload: {
				IDSelectedCustomer: value,
			},
		});
	};

	const setIDRestaurant = (value: TChangeDishPositionSelectedItem) => {
		dispatch({
			type: ChangeDishPositionActionTypes.SET_ID_RESTAURANT,
			payload: {
				IDSelectedRestaurant: value,
			},
		});
	};

	const setIDCategory = (value: TChangeDishPositionSelectedItem) => {
		dispatch({
			type: ChangeDishPositionActionTypes.SET_ID_CATEGORY,
			payload: {
				IDSelectedCategory: value,
			},
		});
	};

	const setIDSubCategory = (value: TChangeDishPositionSelectedItem) => {
		dispatch({
			type: ChangeDishPositionActionTypes.SET_ID_SUB_CATEGORY,
			payload: {
				IDSelectedSubCategory: value,
			},
		});
	};

	const resetSelectedControls = () => dispatch({ type: ChangeDishPositionActionTypes.RESET_SELECTED_CONTROLS });

	const setPositionsData = (value: IDishPositionItem[]) => {
		dispatch({
			type: ChangeDishPositionActionTypes.SET_POSITIONS_DATA,
			payload: {
				positionsData: value,
			},
		});
	};

	const reorderItems = (value: IDishPositionItem[]) => {
		dispatch({
			type: ChangeDishPositionActionTypes.REORDER_ITEMS,
			payload: {
				newOrder: value,
			},
		});
	};

	const changeTypeOfModalWindow = (value: TTypeOfChangeDishPositionModalWindow) => {
		dispatch({
			type: ChangeDishPositionActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	};

	const setVisibleAdditionalRestaurantsModal = (value: boolean) => {
		dispatch({
			type: ChangeDishPositionActionTypes.SET_VISIBLE_ADDITIONAL_RESTAURANTS_MODAL,
			payload: {
				isVisible: value,
				isApplyToOne: value ? false : state.isApplyToOne,
				selectedAdditionalRestaurants: value ? [] : state.selectedAdditionalRestaurants,
			},
		});
	};

	const toggleApplyToOne = () => {
		dispatch({
			type: ChangeDishPositionActionTypes.TOGGLE_APPLY_TO_ONE,
		});
	};

	const setAdditionalRestaurants = (value: string[]) => {
		dispatch({
			type: ChangeDishPositionActionTypes.SET_ADDITIONAL_RESTAURANTS,
			payload: {
				additionalRestaurants: value,
			},
		});
	};

	const setFetchedRestaurants = (value: IItemForGetShortInfo[]) => {
		dispatch({
			type: ChangeDishPositionActionTypes.SET_FETCHED_RESTAURANTS,
			payload: {
				fetchedRestaurants: value,
			},
		});
	};

	useEffect(() => {
		response &&
			dispatch({
				type: ChangeDishPositionActionTypes.SET_POSITIONS_DATA,
				payload: {
					positionsData: response?.data.dishPositions,
				},
			});
	}, [response]);

	useEffect(() => {
		refetch();
	}, [state.IDSelectedSubCategory]);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	return (
		<ChangeDishPositionContext.Provider
			value={{
				setIDCustomer,
				setIDRestaurant,
				setIDCategory,
				setIDSubCategory,
				resetSelectedControls,
				setPositionsData,
				reorderItems,
				changeTypeOfModalWindow,
				setVisibleAdditionalRestaurantsModal,
				toggleApplyToOne,
				setAdditionalRestaurants,
				setFetchedRestaurants,
				IDSelectedCustomer: state.IDSelectedCustomer,
				IDSelectedRestaurant: state.IDSelectedRestaurant,
				IDSelectedCategory: state.IDSelectedCategory,
				IDSelectedSubCategory: state.IDSelectedSubCategory,
				positionsData: state.positionsData,
				typeOfModalWindow: state.typeOfModalWindow,
				isVisibleAdditionalRestaurantsModal: state.isVisibleAdditionalRestaurantsModal,
				isApplyToOne: state.isApplyToOne,
				selectedAdditionalRestaurants: state.selectedAdditionalRestaurants,
				fetchedRestaurants: state.fetchedRestaurants,
			}}>
			{children}
		</ChangeDishPositionContext.Provider>
	);
};

export default ChangeDishPositionState;
